import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Zap, TrendingUp, Truck, Hash, Armchair, Smartphone, Globe } from "lucide-react";
import { startResearch, listRegions } from "../lib/api";

const NICHES = [
  {
    id: "home_decor",
    label: "Home Decor",
    description: "Cozy, aesthetic, space-saving decor for modern Canadian homes.",
    Icon: Armchair,
  },
  {
    id: "gadgets_tech",
    label: "Gadgets / Tech",
    description: "Problem-solving consumer electronics and smart-home gear.",
    Icon: Smartphone,
  },
];

export default function Dashboard() {
  const [selectedNiche, setSelectedNiche] = useState("home_decor");
  const [selectedRegion, setSelectedRegion] = useState("CA-ON");
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    listRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  const handleRun = async () => {
    setLoading(true);
    try {
      const job = await startResearch(selectedNiche, selectedRegion);
      toast.success("Research pipeline started", { description: `Job ${job.id.slice(0, 8)}` });
      navigate(`/admin/research/${job.id}`);
    } catch (e) {
      toast.error("Failed to start research", { description: e?.message });
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
      {/* Hero */}
      <div className="grid md:grid-cols-12 gap-0 border border-zinc-200">
        <div className="md:col-span-8 p-8 md:p-12 border-r border-zinc-200 relative grain overflow-hidden">
          <div className="micro-label mb-6 flex items-center gap-2">
            <span className="w-2 h-2 bg-[#002FA7] blink-dot" />
            LIVE · ONTARIO · CANADA
          </div>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6"
            style={{ fontFamily: "Outfit" }}
            data-testid="hero-heading"
          >
            FIND THE NEXT<br />
            <span className="klein-text">WINNING PRODUCT.</span>
          </h1>
          <p className="text-lg text-zinc-600 max-w-xl leading-relaxed mb-2">
            An autonomous research agent that scans TikTok and Instagram trend signals,
            cross-references the CJ Dropshipping catalog, and surfaces 3 problem-solving
            products with <span className="font-bold text-zinc-900">50%+ margins</span> and{" "}
            <span className="font-bold text-zinc-900">≤7 day shipping</span> to Ontario.
          </p>
        </div>
        <div className="md:col-span-4 flex flex-col">
          <StatCell label="Profit Target" value="50%+" Icon={TrendingUp} />
          <StatCell label="Max Shipping" value="7 DAYS" Icon={Truck} />
          <StatCell label="Signal Sources" value="3" Icon={Hash} last />
        </div>
      </div>

      {/* Niche Selector */}
      <div className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            01 · Select Niche
          </h2>
          <span className="micro-label hidden md:inline">Step 1 of 3</span>
        </div>
        <div className="grid md:grid-cols-2 gap-0 border border-zinc-200">
          {NICHES.map((n, idx) => {
            const active = selectedNiche === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setSelectedNiche(n.id)}
                data-testid={`niche-${n.id}`}
                className={`text-left p-8 md:p-12 transition-all relative ${
                  idx === 0 ? "border-r border-zinc-200" : ""
                } ${active ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <n.Icon size={48} strokeWidth={active ? 2.5 : 2} />
                  <div
                    className={`w-5 h-5 border-2 ${
                      active ? "border-white bg-[#002FA7]" : "border-zinc-400"
                    }`}
                  />
                </div>
                <div
                  className="text-4xl md:text-5xl font-black tracking-tighter mb-2"
                  style={{ fontFamily: "Outfit" }}
                >
                  {n.label}
                </div>
                <p className={`text-sm leading-relaxed ${active ? "text-zinc-300" : "text-zinc-600"}`}>
                  {n.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Region Selector */}
      <div className="mt-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            02 · Target Region
          </h2>
          <span className="micro-label hidden md:inline">Step 2 of 3</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200">
          {regions.map((r, idx) => {
            const active = selectedRegion === r.code;
            const isLast = idx === regions.length - 1;
            return (
              <button
                key={r.code}
                onClick={() => setSelectedRegion(r.code)}
                data-testid={`region-${r.code}`}
                className={`text-left p-5 md:p-6 transition-all relative ${
                  !isLast ? "border-r border-zinc-200" : ""
                } ${idx < 2 ? "border-b md:border-b-0 border-zinc-200" : ""} ${
                  active ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"
                }`}
              >
                <Globe size={24} strokeWidth={active ? 2.5 : 2} className="mb-4" />
                <div
                  className="text-xl md:text-2xl font-black tracking-tighter mb-1"
                  style={{ fontFamily: "Outfit" }}
                >
                  {r.code}
                </div>
                <div className={`text-xs ${active ? "text-zinc-300" : "text-zinc-500"} mb-2`}>
                  {r.label}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider">
                  ≤ {r.max_shipping_days}d
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Run */}
      <div className="mt-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            03 · Launch Research
          </h2>
          <span className="micro-label hidden md:inline">Step 3 of 3</span>
        </div>
        <div className="border border-zinc-200 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="micro-label mb-2">Targeting</div>
            <div className="text-xl md:text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
              {NICHES.find((n) => n.id === selectedNiche)?.label} ·{" "}
              <span className="klein-text">
                {regions.find((r) => r.code === selectedRegion)?.label || selectedRegion}
              </span>
            </div>
          </div>
          <button
            onClick={handleRun}
            disabled={loading}
            data-testid="run-research-btn"
            className="btn-klein inline-flex items-center gap-3 text-sm scan-pulse disabled:scan-pulse-none"
          >
            {loading ? (
              <>PREPARING AGENT...</>
            ) : (
              <>
                <Zap size={18} fill="currentColor" />
                RUN RESEARCH AGENT
                <ArrowRight size={18} strokeWidth={2.5} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-20">
        <div className="micro-label mb-4">HOW IT WORKS</div>
        <div className="grid md:grid-cols-3 gap-0 border border-zinc-200">
          {[
            { n: "01", t: "Scan", d: "TikTok + Instagram hashtags scraped for viral signals in your niche." },
            { n: "02", t: "Cross-reference", d: "CJ Dropshipping catalog matched against trending patterns." },
            { n: "03", t: "Analyze", d: "Claude Sonnet 4.5 ranks winners by margin, shipping, and problem-fit." },
          ].map((s, i) => (
            <div key={s.n} className={`p-8 ${i < 2 ? "border-r border-zinc-200" : ""}`}>
              <div
                className="text-6xl font-black tracking-tighter klein-text mb-3"
                style={{ fontFamily: "Outfit" }}
              >
                {s.n}
              </div>
              <div className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>
                {s.t}
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, Icon, last = false }) {
  return (
    <div
      className={`flex-1 p-6 flex items-center justify-between ${
        last ? "" : "border-b border-zinc-200"
      }`}
    >
      <div>
        <div className="micro-label mb-1">{label}</div>
        <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
          {value}
        </div>
      </div>
      <Icon size={28} strokeWidth={2} className="text-zinc-400" />
    </div>
  );
}
